import { useCallback, useRef, useState } from 'react';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { pollForNewPaymentMethod } from '../../../financial-services-ui/poll-for-new-payment-method';
import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';

type UsePaymentMethodCreationProps = {
  initialPaymentMethodCount: number;
  reloadData?: () => Promise<[unknown, PaymentMethodDetails[] | undefined]>;
  onSuccess: (paymentMethodId: string, billingInfo?: BillingInfoData) => void;
  onError: () => void;
};

export const usePaymentMethodCreation = ({ initialPaymentMethodCount, reloadData, onSuccess, onError }: UsePaymentMethodCreationProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Use ref to track the latest billing info without causing re-renders
  const billingInfoRef = useRef<BillingInfoData | undefined>();

  const handlePaymentSuccess = useCallback(async () => {
    if (!reloadData) {
      onError();
      return;
    }

    setIsProcessing(true);

    try {
      // Poll for new payment method (10 retries × 3s = 30s max)
      const [, freshPaymentMethods] = await pollForNewPaymentMethod({
        reloadData,
        previousCount: initialPaymentMethodCount,
      });

      // Find newly added payment method
      if (!freshPaymentMethods || freshPaymentMethods.length <= initialPaymentMethodCount) {
        throw new Error('No new payment method detected after polling');
      }

      // Extract the new payment method (last one added)
      const newPaymentMethod = freshPaymentMethods[freshPaymentMethods.length - 1];

      if (!newPaymentMethod?.id) {
        throw new Error('New payment method missing ID');
      }

      // Extract billing info from the payment method's accountHolderAddress
      // This is more reliable than FSUI context which is missing fields like 'state'
      let billingInfoFromPaymentMethod: BillingInfoData | undefined;

      if (newPaymentMethod?.accountHolderAddress) {
        // Use the helper to extract billing info from payment method
        const { getBillingInfoFromPaymentMethod } = await import('../../../financial-services-ui/mapping');

        const extracted = getBillingInfoFromPaymentMethod(newPaymentMethod, {
          name: '', // Will be populated from form
          homeCountry: 'US',
          phone: '', // Will be populated from form
          companyName: '', // Will be populated from form
        });

        billingInfoFromPaymentMethod = extracted ?? undefined;
      }

      // Notify parent with billing info extracted from payment method (has complete address including state)
      onSuccess(newPaymentMethod.id, billingInfoFromPaymentMethod);
    } catch {
      // Error notification handled by onError callback
      onError();
    } finally {
      setIsProcessing(false);
    }
  }, [reloadData, initialPaymentMethodCount, onSuccess, onError]);

  // Update billing info ref when it changes
  const updateBillingInfoRef = useCallback((billingInfo?: BillingInfoData) => {
    billingInfoRef.current = billingInfo;
  }, []);

  return {
    isProcessing,
    handlePaymentSuccess,
    updateBillingInfoRef,
  };
};
